import React, { useState } from 'react';
import {
  Box, Typography, Grid, Paper, Stack, Chip, Divider, Button, Card, CardContent,
  List, ListItem, ListItemText, Alert, Menu, MenuItem, IconButton, ListItemIcon,
} from '@mui/material';
import {
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  LocalHospital as InsuranceIcon,
  ContactEmergency as EmergencyIcon,
  MedicalServices as MedicalIcon,
  EventAvailable as EventIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

const statusColors = {
  New: 'info',
  Active: 'success',
  Inactive: 'default',
  Deceased: 'error',
  Transferred: 'warning',
};

function PatientDetail({ patient, onEdit }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!patient) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Select a patient to view details
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {patient.fullName || `${patient.firstName} ${patient.lastName}`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label={patient.status || 'New'}
              color={statusColors[patient.status] || 'default'}
              size="small"
            />
            {patient.age && (
              <Chip
                icon={<CakeIcon />}
                label={`${patient.age} years`}
                size="small"
                variant="outlined"
              />
            )}
            {patient.gender && (
              <Chip
                label={patient.gender}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => onEdit(patient)}
        >
          Edit
        </Button>
      </Stack>

      {/* Flags/Alerts */}
      {patient.flags && patient.flags.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {patient.flags.map((flag) => flag.type).join(', ')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {patient.email && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon color="action" />
                    <Typography variant="body2">{patient.email}</Typography>
                  </Stack>
                )}
                {patient.phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon color="action" />
                    <Typography variant="body2">{patient.phone}</Typography>
                  </Stack>
                )}
                {patient.alternatePhone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon color="action" />
                    <Typography variant="body2">
                      {patient.alternatePhone}
                      {' '}
                      (Alt)
                    </Typography>
                  </Stack>
                )}
                {patient.address && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Address</Typography>
                    <Typography variant="body2">
                      {patient.address.street}
                      <br />
                      {patient.address.city}
                      ,
                      {patient.address.state}
                      {' '}
                      {patient.address.zipCode}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Insurance Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Insurance
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {patient.insurance ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Provider</Typography>
                    <Typography variant="body2">{patient.insurance.provider || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Policy Number</Typography>
                    <Typography variant="body2">{patient.insurance.policyNumber || '-'}</Typography>
                  </Box>
                  {patient.insurance.verified && (
                    <Chip
                      label={`Verified ${patient.insurance.verifiedDate ? new Date(patient.insurance.verifiedDate).toLocaleDateString() : ''}`}
                      color="success"
                      size="small"
                    />
                  )}
                  {patient.insurance.copay && (
                    <Box>
                      <Typography variant="caption" color="textSecondary">Copay</Typography>
                      <Typography variant="body2">
                        $
                        {patient.insurance.copay}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Typography color="textSecondary">No insurance information</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Emergency Contact */}
        {patient.emergencyContact && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Emergency Contact
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Name</Typography>
                    <Typography variant="body2">{patient.emergencyContact.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Relationship</Typography>
                    <Typography variant="body2">{patient.emergencyContact.relationship}</Typography>
                  </Box>
                  {patient.emergencyContact.phone && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">{patient.emergencyContact.phone}</Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Medical Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Medical Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {patient.bloodType && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Blood Type</Typography>
                    <Typography variant="body2">{patient.bloodType}</Typography>
                  </Box>
                )}
                {patient.allergies && patient.allergies.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Allergies</Typography>
                    {patient.allergies.map((allergy, idx) => (
                      <Chip
                        key={idx}
                        label={`${allergy.allergen} (${allergy.severity})`}
                        size="small"
                        color={allergy.severity === 'Severe' ? 'error' : 'warning'}
                        sx={{ mr: 0.5, mt: 0.5 }}
                      />
                    ))}
                  </Box>
                )}
                {patient.chronicConditions && patient.chronicConditions.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Chronic Conditions</Typography>
                    {patient.chronicConditions.map((condition, idx) => (
                      <Chip
                        key={idx}
                        label={condition}
                        size="small"
                        sx={{ mr: 0.5, mt: 0.5 }}
                      />
                    ))}
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Visit History */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Visit History
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Visits</Typography>
                    <Typography variant="h6">{patient.totalVisits || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Missed</Typography>
                    <Typography variant="h6" color="error.main">{patient.missedAppointments || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Cancelled</Typography>
                    <Typography variant="h6">{patient.cancelledAppointments || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Last Visit</Typography>
                    <Typography variant="body2">
                      {patient.lastVisitDate
                        ? new Date(patient.lastVisitDate).toLocaleDateString()
                        : 'Never'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            onPatientEdit(menuPatient);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Patient</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            onPatientDelete(menuPatient);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete Patient</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default PatientDetail;
